import React, {useState} from 'react';
import PropTypes from 'prop-types';
import sessionStore from 'client/Session/SessionStore';
import classnames from 'classnames';
import moment from 'moment';
import appStore from 'client/AppStore';
import CustomerFeedbackDialogue from 'client/CustomerFeedback/CustomerFeedbackDialogue.react';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {UserModelV2} from 'resources/augmented/User/UserModel.v2';
import {UpgradeButton} from 'generic/UpgradeSubjectButton/UpgradeSubjectButton.react';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {Modal, NavItem, Menu, Icon, Button, Text} from '@albert-io/atomic';
import {useTracking} from 'react-tracking';
import NotificationBadge from 'client/GlobalNavigation/NavWidgets/NotificationBadge.react';

import PointsIndicator from '../PointsIndicator/PointsIndicator';
import globalNavigationStore from '../GlobalNavigationStore';

import icon from './feedback-icon.svg?raw';
import classNames from 'classnames';

/**
 * Checks the session store and application state to determine whether or not the
 * current session is able to leave feedback.
 */
function canLeaveFeedback() {
  return sessionStore.isTeacher() || sessionStore.isSuper();
}

const NavWidgets = () => {
  if (appStore.isZoneDennis()) {
    return null;
  }

  return (
    <>
      {globalNavigationStore.shouldShowUpgradeButton() ? (
        <li>
          <NavItem stateless>
            <UpgradeButton />
          </NavItem>
        </li>
      ) : null}
      {sessionStore.isStudent() ? <HelpCenterLink /> : <HelpMenu />}
      {sessionStore.hasValidSession() && sessionStore.isStudent() ? (
        <li>
          <NavItem stateless as='span'>
            <PointsIndicator />
          </NavItem>
        </li>
      ) : null}

      {
        /**
         * We only render the `<FeedbackWidget`> if the session and application state
         * _can_ leave feedback.
         */
        canLeaveFeedback() && <FeedbackWidget />
      }
    </>
  );
};

export const HelpMenu = ({isForDrawer = false, wrapperClassName}) => {
  const {trackEvent} = useTracking({
    event: 'click',
    type: 'link',
    feature: null,
    component: 'primary nav',
    content: 'Help Center'
  });

  const isMatchingRoute = window.location.pathname.startsWith('/resource-hub');

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const linkClasses = classNames('global-nav-drawer__link', {
    'global-nav-drawer__link--active': isMatchingRoute
  });

  if (isForDrawer) {
    return (
      <>
        <Button
          className={`${linkClasses} global-nav-drawer__assignments-dropdown w-full`}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          Help & Resources
          <Icon icon={dropdownOpen ? 'caret-up' : 'caret-down'} className='ml-x1' />
        </Button>
        {dropdownOpen && (
          <>
            <a
              href='https://help.albert.io'
              className={`${linkClasses} global-nav-drawer__assignments-subitem`}
              onClick={(e) => {
                e.stopPropagation();
                trackEvent();
              }}
            >
              Help Docs
            </a>
            {sessionStore.hasValidSession() && !sessionStore.isStudent() && (
              <a
                href='/resource-hub/'
                className={`${linkClasses} global-nav-drawer__assignments-subitem`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Resource Hub
              </a>
            )}
            <a
              href='https://app.lemcal.com/@albert-adoption-team/teacher-talk'
              className={`${linkClasses} global-nav-drawer__assignments-subitem`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Live Teacher Support Session
            </a>
          </>
        )}
      </>
    );
  }

  return (
    <Menu wrapperClassName={wrapperClassName} closeOnItemClick>
      <Menu.Trigger>
        {(triggerProps) => (
          <>
            <NavItem {...triggerProps} as='div' active={isMatchingRoute}>
              Help & Resources
              <Icon icon='caret-down' className='ml-x1' />
            </NavItem>
            <NotificationBadge className='top-x0-half right-x0' />
          </>
        )}
      </Menu.Trigger>
      <Menu.Content className='global-nav__help-menu'>
        <Menu.Item
          leftIconProps={{icon: 'life-ring'}}
          onClick={() => {
            trackEvent();
            window.location.href = 'https://help.albert.io';
          }}
        >
          Help Docs
        </Menu.Item>
        {sessionStore.hasValidSession() && !sessionStore.isStudent() && (
          <Menu.Item
            leftIconProps={{icon: 'graduation-cap'}}
            onClick={() => (window.location.href = '/resource-hub/')}
          >
            Resource Hub
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          className='global-nav__schedule-icon'
          leftIconProps={{icon: 'comments'}}
          onClick={() => {
            const today = moment().format('YYYY-MM-DD');
            localStorage.setItem('teacher_talk_notification_date_last_clicked', today);
            window.location.href = 'https://app.lemcal.com/@albert-adoption-team/teacher-talk';
          }}
        >
          <div className='flex flex-1 flex-col'>
            <Text size='m'>Live Teacher Support Session</Text>
            <Text color='tertiary' size='xs'>
              Book your free 15-minute personalized training.
            </Text>
            <NotificationBadge className='top-x0 right-x0' />
          </div>
        </Menu.Item>
      </Menu.Content>
    </Menu>
  );
};

export const HelpCenterLink = (props) => {
  const {trackEvent} = useTracking({
    event: 'click',
    type: 'link',
    feature: null,
    component: 'primary nav',
    content: 'Help Center'
  });
  return (
    <NavItem
      {...props}
      icon='question-circle'
      href='https://help.albert.io'
      onClick={() => trackEvent()}
    >
      Help
    </NavItem>
  );
};

const FeedbackWidget = awaitMandarkQueries(
  () => ({
    queries: {
      user: resource('users_v2').mandarkEndpoint(['users_v2', sessionStore.getUserId()])
    },
    loadingElement: <div />
  }),
  class FeedbackWidget extends React.PureComponent {
    static propTypes = {
      user: PropTypes.instanceOf(UserModelV2)
    };

    constructor() {
      super();

      this.state = {
        shouldIconPulse: true,
        clearIcon: false,
        showModal: false
      };
    }

    handleToggle = () => {
      this.setState((state) => ({showModal: !state.showModal}));
    };

    handleClick = () => {
      this.setState({shouldIconPulse: false});
      this.handleToggle();
    };

    render() {
      const {user} = this.props;

      const showRatingIcon =
        canLeaveFeedback() &&
        user.getCustomerFeedbackReminderDate() !== null &&
        user.getCustomerFeedbackReminderDate() <= moment();

      if (!showRatingIcon) {
        return null;
      }

      const ratingIconClassNames = classnames({
        'global-nav__rating-icon': !this.state.shouldIconPulse,
        'global-nav__rating-icon-pulsing': this.state.shouldIconPulse
      });

      return (
        <li>
          {!this.state.clearIcon && (
            <NavItem
              className='global-nav__rating-icon-container'
              role='button'
              tabIndex={0}
              as='div'
              active={this.state.showModal}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  this.handleClick();
                }
              }}
              onClick={this.handleClick}
            >
              <div className={ratingIconClassNames} dangerouslySetInnerHTML={{__html: icon}} />
            </NavItem>
          )}
          {this.state.showModal && (
            <Modal
              className='customer-feedback-modal'
              ariaLabel='Customer Feedback Modal'
              handleClose={this.handleToggle}
              longModal
            >
              {() => (
                <CustomerFeedbackDialogue
                  name={user.getFirstName()}
                  handleClose={this.handleToggle}
                  inModal
                  fromNavbar
                  clearIcon={() => this.setState({clearIcon: true})}
                />
              )}
            </Modal>
          )}
        </li>
      );
    }
  }
);

export default NavWidgets;
